import {
    ArrowPathIcon,
    MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
    Button,
    Input,
    Select,
    SelectItem,
    Tooltip,
} from "@heroui/react";
import React, { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatEnumString, formatSportName, formatWeightClassName } from "../../utils/utilMethods";

interface FeedsFiltersProps {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    selectedSport: string;
    setSelectedSport: (sport: string) => void;
    selectedWeight: string;
    setSelectedWeight: (weight: string) => void;
    selectedManagerType: string;
    setSelectedManagerType: (type: string) => void;
    selectedProfileType: string;
    setSelectedProfileType: (type: string) => void;
    sports: string[];
    weightClasses: string[];
    loadSportsIfNeeded: () => Promise<void>;
    loadWeightClassesIfNeeded: () => Promise<void>;
    resetFilters: () => void;
    activeTab: "all" | "my";
}

const FeedsFiltersComponent: React.FC<FeedsFiltersProps> = ({
    searchQuery,
    setSearchQuery,
    selectedSport,
    setSelectedSport,
    selectedWeight,
    setSelectedWeight,
    selectedManagerType,
    setSelectedManagerType,
    selectedProfileType,
    setSelectedProfileType,
    sports,
    weightClasses,
    loadSportsIfNeeded,
    loadWeightClassesIfNeeded,
    resetFilters,
    activeTab,
}) => {
    const { t } = useTranslation("feeds");
    const managerTypeOptions = ["INDIVIDUAL", "AGENCY"];
    const profileTypeOptions = ["FIGHTER", "MANAGER"];

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(e.target.value);
        },
        [setSearchQuery]
    );

    const handleSportChange = useCallback(
        (key: any) => {
            setSelectedSport(Array.from(key)[0] as string);
        },
        [setSelectedSport]
    );

    const handleWeightChange = useCallback(
        (key: any) => {
            setSelectedWeight(Array.from(key)[0] as string);
        },
        [setSelectedWeight]
    );

    const handleManagerTypeChange = useCallback(
        (key: any) => {
            setSelectedManagerType(Array.from(key)[0] as string);
        },
        [setSelectedManagerType]
    );

    const handleProfileTypeChange = useCallback(
        (key: any) => {
            setSelectedProfileType(Array.from(key)[0] as string);
        },
        [setSelectedProfileType]
    );

    const handleSportOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                loadSportsIfNeeded();
            }
        },
        [loadSportsIfNeeded]
    );

    const handleWeightOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                loadWeightClassesIfNeeded();
            }
        },
        [loadWeightClassesIfNeeded]
    );

    return (
        <div className="flex items-center gap-3 flex-nowrap w-full">
            <Input
                placeholder={t("search_by_title")}
                value={searchQuery}
                onChange={handleSearchChange}
                startContent={<MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />}
                className="min-w-[150px] flex-1"
                size="sm"
            />

            {activeTab === "all" && (
                <>
                    <Select
                        placeholder={t("select_sport")}
                        selectedKeys={selectedSport ? [selectedSport] : []}
                        onSelectionChange={handleSportChange}
                        onOpenChange={handleSportOpenChange}
                        className="min-w-[120px] flex-1"
                        size="sm"
                    >
                        {sports.length > 0 ? (
                            sports.map((sport) => (
                                <SelectItem key={sport}>{formatSportName(sport)}</SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-sport" isDisabled>
                                {t("no_sports_available")}
                            </SelectItem>
                        )}
                    </Select>
                    <Select
                        placeholder={t("select_weight_class")}
                        selectedKeys={selectedWeight ? [selectedWeight] : []}
                        onSelectionChange={handleWeightChange}
                        onOpenChange={handleWeightOpenChange}
                        className="min-w-[120px] flex-1"
                        size="sm"
                    >
                        {weightClasses.length > 0 ? (
                            weightClasses.map((weight) => (
                                <SelectItem key={weight}>{formatWeightClassName(weight)}</SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-weight" isDisabled>
                                {t("no_weight_classes_available")}
                            </SelectItem>
                        )}
                    </Select>
                    <Select
                        placeholder={t("select_manager_type")}
                        selectedKeys={selectedManagerType ? [selectedManagerType] : []}
                        onSelectionChange={handleManagerTypeChange}
                        className="min-w-[120px] flex-1"
                        size="sm"
                    >
                        {managerTypeOptions.length > 0 ? (
                            managerTypeOptions.map((type) => (
                                <SelectItem key={type}>{formatEnumString(type)}</SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-manager-type" isDisabled>
                                {t("no_manager_types_available")}
                            </SelectItem>
                        )}
                    </Select>
                    <Select
                        placeholder={t("select_profile_type")}
                        selectedKeys={selectedProfileType ? [selectedProfileType] : []}
                        onSelectionChange={handleProfileTypeChange}
                        className="min-w-[120px] flex-1"
                        size="sm"
                    >
                        {profileTypeOptions.length > 0 ? (
                            profileTypeOptions.map((type) => (
                                <SelectItem key={type}>{formatEnumString(type)}</SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-profile-type" isDisabled>
                                {t("no_profile_types_available")}
                            </SelectItem>
                        )}
                    </Select>
                </>
            )}

            <Tooltip content={t("reset_filters")}>
                <Button
                    isIconOnly
                    variant="light"
                    onClick={resetFilters}
                    className="h-10 w-10 text-gray-600 hover:text-gray-800"
                    size="sm"
                >
                    <ArrowPathIcon className="w-5 h-5" />
                </Button>
            </Tooltip>
        </div>
    );
};

export const FeedsFilters = memo(FeedsFiltersComponent);